/**
 * © 2026 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * ResumePrompt Component
 * specific feature for non-intrusive resume functionality.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaPlay } from 'react-icons/fa';
import { useUser } from '@/hooks/useUser';
import { useModal } from '@/context/ModalContext';
import { useNavContext } from '@/context/NavigationProvider';
import { getLastWatched } from '@/services/playback.service';
import { getFilmById } from '@/services/content.service';

const ResumePrompt = () => {
    const { t } = useTranslation();
    const { user } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const { cookieBannerHeight, dashboardSidebarState } = useNavContext();
    const { videoCloseCount } = useModal();

    const [isVisible, setIsVisible] = useState(false);
    const [videoData, setVideoData] = useState(null);
    const [dismissedEvent, setDismissedEvent] = useState(null); // { userId, filmId, lastUpdated }

    useEffect(() => {
        // 1. Auth Check
        if (!user) {
            setIsVisible(false);
            return;
        }

        let isMounted = true;

        const checkResumeStatus = async () => {
            // 2. Fetch Status
            // Lightweight fetch first - we don't fetch heavy metadata unless we need to show it.
            const lastWatched = await getLastWatched(user.uid);

            // 3. Async Safety Check #1
            if (!isMounted || !lastWatched) {
                return;
            }

            // 4. PRESERVE VALIDATION LOGIC
            // Only show if engaged (>30s) and not finished (<95%)
            const { currentTime, duration, filmId } = lastWatched;
            if (!duration || currentTime < 30 || (currentTime / duration) > 0.95) {
                if (isMounted) setIsVisible(false);
                return;
            }

            // 5. CONVERT TIMESTAMP (Primitive)
            // Critical for correct comparison logic across rerenders/navigation
            const rawTimestamp = lastWatched.lastUpdated;
            const remoteLastUpdated = rawTimestamp?.toMillis
                ? rawTimestamp.toMillis()
                : (typeof rawTimestamp === 'number' ? rawTimestamp : 0);

            // 6. CHECK ACTIVE PLAYER (Before Show)
            // Prevent flicker by checking if we are on the player page for this specific video
            const searchParams = new URLSearchParams(location.search);
            if (searchParams.get('play') === filmId) {
                if (isMounted) setIsVisible(false);
                return;
            }

            // 7. CHECK DISMISSAL (Before Metadata Fetch - Optimization)
            if (dismissedEvent &&
                dismissedEvent.userId === user.uid &&
                dismissedEvent.filmId === filmId &&
                dismissedEvent.lastUpdated === remoteLastUpdated
            ) {
                return; // Keep hidden: User dismissed this exact version of the video
            }

            // 8. FETCH METADATA (Only if passing all checks)
            const filmMetadata = await getFilmById(filmId);

            if (filmMetadata && isMounted) {
                setVideoData({
                    filmId,
                    title: filmMetadata.title,
                    thumbnail: filmMetadata.thumbnailUrl || filmMetadata.img, // fallback checks
                    timestamp: currentTime,
                    duration,
                    lastUpdated: remoteLastUpdated // STRICTLY ADDED TO STATE
                });
                // Delay showing slightly for smooth entrance
                setTimeout(() => {
                    if (isMounted) setIsVisible(true);
                }, 1000);
            }
        };

        // Delay slightly to allow Firestore writes to settle
        // (StreamPlayer's cleanup save is fire-and-forget)
        const timerId = setTimeout(() => {
            checkResumeStatus();
        }, 500);

        return () => {
            isMounted = false;
            clearTimeout(timerId);
        };
    }, [user, location.pathname, location.search, dismissedEvent, videoCloseCount]);

    const handleClose = (e) => {
        e.stopPropagation();
        if (videoData && user) {
            // Dismiss for this specific version of the video session
            setDismissedEvent({
                userId: user.uid,
                filmId: videoData.filmId,
                lastUpdated: videoData.lastUpdated
            });
        }
        setIsVisible(false);
    };

    const handleResume = () => {
        if (!videoData) return;
        // Navigate to dashboard with deep links
        navigate(`/dashboard?play=${videoData.filmId}&time=${Math.floor(videoData.timestamp)}`);
        // Optimistically hide
        setIsVisible(false);
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        return `${m}m`;
    };

    // --- Dynamic Positioning Logic ---
    // Calculates the left offset based on sidebar state AND current route
    const getLeftOffset = () => {
        // Base padding (p-6 = 24px)
        const basePadding = 24;

        // CRITICAL FIX: Only respect sidebar state if we are actually ON a dashboard page.
        // If we are on Home, FAQ, etc., the sidebar is not visible, so we should NOT add offset.
        const hasSidebar = location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/settings');

        if (!hasSidebar) {
            return basePadding;
        }

        switch (dashboardSidebarState) {
            case 'open':
                return 256 + basePadding; // 64 (w-64) + padding
            case 'collapsed':
                return 80 + basePadding; // 20 (w-20) + padding
            case 'closed':
            default:
                return basePadding;
        }
    };

    if (!isVisible || !videoData) return null;

    const leftPosition = getLeftOffset();
    const bottomPosition = 24 + cookieBannerHeight;

    return (
        <div
            className="fixed z-[900] bg-card border border-muted rounded-lg shadow-2xl flex items-center gap-4 p-3 pr-8 cursor-pointer transition-all duration-300 hover:scale-105 hover:border-primary/50 animate-slideUp"
            style={{
                left: `${leftPosition}px`,
                bottom: `${bottomPosition}px`,
                maxWidth: '320px'
            }}
            onClick={handleResume}
            role="alert"
        >
            {/* Header Text */}
            <div className="flex flex-col w-full">
                <span className="text-xs font-bold text-gray-400 pl-1 mb-2">
                    {t('resumePrompt.continueWatching', 'Continue watching?')}
                </span>

                <div className="flex items-center gap-4">
                    {/* Thumbnail */}
                    <div className="relative w-20 h-12 rounded bg-black overflow-hidden flex-shrink-0">
                        <img
                            src={videoData.thumbnail || 'https://placehold.co/80x48/000/FFF?text=Video'}
                            alt={videoData.title}
                            className="w-full h-full object-cover opacity-80"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <FaPlay className="text-white text-xs drop-shadow-md" />
                        </div>
                    </div>

                    {/* Text Info */}
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white truncate leading-tight">
                            {videoData.title}
                        </p>
                        <p className="text-xs text-primary font-medium mt-0.5">
                            {t('resumePrompt.resumeFrom', 'Resume from {{time}}', { time: formatTime(videoData.timestamp) })}
                        </p>
                    </div>
                </div>
            </div>

            {/* Close Button */}
            <button
                onClick={handleClose}
                className="absolute top-1 right-1 p-1.5 text-text-secondary hover:text-white rounded-full hover:bg-white/10 transition-colors"
                aria-label={t('common.close')}
            >
                <FaTimes size={12} />
            </button>
        </div>
    );
};

export default ResumePrompt;
